import * as THREE from "three";

export interface SecretPass {
  position: THREE.Vector3;
}

interface SecretRing {
  mesh: THREE.Mesh;
  inner: THREE.Mesh;
  passed: boolean;
}

const RADIUS = 1.6;       // 1.5× regular ring radius — generous to thread
const TUBE = 0.08;
const PICKUP_LATERAL = 1.9;
const SPAWN_MIN_S = 45;
const SPAWN_MAX_S = 75;

/**
 * Rare "secret" gold rings along the flight path. Sibling to the regular
 * RingField — managed separately so rings.ts internals stay untouched.
 *
 * Effect on pass: pure visual fanfare (heavy particle burst + chime).
 * No scoring or time impact; server replay is unaffected.
 *
 * Spawn cadence is randomized 45-75s so a typical 30s run never sees one;
 * Practice / Daily / Speedrun runs that drag on get a moment of reward.
 */
export class SecretRings {
  readonly group = new THREE.Group();
  private rings: SecretRing[] = [];
  private rng: () => number;
  private nextSpawnInS = 0;
  private elapsed = 0;

  constructor(seed = 0) {
    this.rng = makeRng(seed ^ 0xa11ce5e3);
    this.scheduleNext(true);
  }

  reset(seed: number) {
    this.rng = makeRng(seed ^ 0xa11ce5e3);
    this.elapsed = 0;
    this.nextSpawnInS = 0;
    for (const r of this.rings) this.dispose(r);
    this.rings.length = 0;
    this.scheduleNext(true);
  }

  update(dt: number, shipPos: THREE.Vector3): SecretPass[] {
    this.elapsed += dt;
    const events: SecretPass[] = [];

    if (this.elapsed >= this.nextSpawnInS) {
      this.spawn(shipPos);
      this.scheduleNext();
    }

    for (const r of this.rings) {
      if (r.passed) continue;
      r.mesh.rotation.z += dt * 0.4;
      if (shipPos.z <= r.mesh.position.z + 0.2) {
        const dx = r.mesh.position.x - shipPos.x;
        const dy = r.mesh.position.y - shipPos.y;
        if (Math.hypot(dx, dy) < PICKUP_LATERAL) {
          r.passed = true;
          events.push({ position: r.mesh.position.clone() });
        }
      }
    }

    for (let i = this.rings.length - 1; i >= 0; i--) {
      const r = this.rings[i];
      if (r.passed || r.mesh.position.z > shipPos.z + 18) {
        this.dispose(r);
        this.rings.splice(i, 1);
      }
    }

    return events;
  }

  private spawn(shipPos: THREE.Vector3) {
    const z = shipPos.z - 80 - this.rng() * 30;
    const x = (this.rng() - 0.5) * 10;
    const y = (this.rng() - 0.5) * 4;

    const ringGeo = new THREE.TorusGeometry(RADIUS, TUBE, 10, 64);
    const ringMat = new THREE.MeshStandardMaterial({
      color: 0xffd54a,
      emissive: 0xffaa11,
      emissiveIntensity: 2.4,
      metalness: 0.6,
      roughness: 0.2,
    });
    const mesh = new THREE.Mesh(ringGeo, ringMat);
    mesh.position.set(x, y, z);

    const innerGeo = new THREE.RingGeometry(RADIUS - 0.2, RADIUS + 0.2, 64);
    const innerMat = new THREE.MeshBasicMaterial({
      color: 0xffe88c,
      transparent: true,
      opacity: 0.4,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const inner = new THREE.Mesh(innerGeo, innerMat);
    mesh.add(inner);

    this.group.add(mesh);
    this.rings.push({ mesh, inner, passed: false });
  }

  private dispose(r: SecretRing) {
    this.group.remove(r.mesh);
    r.mesh.geometry.dispose();
    (r.mesh.material as THREE.Material).dispose();
    r.inner.geometry.dispose();
    (r.inner.material as THREE.Material).dispose();
  }

  private scheduleNext(now = false) {
    const dur = SPAWN_MIN_S + this.rng() * (SPAWN_MAX_S - SPAWN_MIN_S);
    this.nextSpawnInS = (now ? 20 : this.elapsed) + dur;
  }
}

function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
